import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/api/client";
import type { LifecycleStateLabel } from "@/api/projects";
// The active-state set has ONE home (`api/runs.ts`). This module carried two
// more hand-written copies of the same seven labels, one per polling gate, and
// `RunHeader` a third — three lists that could disagree about whether a run is
// still moving, which is exactly the drift VIS/V1 was told not to add a fourth
// of. Importing here is safe in both directions: `api/runs.ts` imports only
// `api/client` and `api/projects`, so there is no cycle.
import { ACTIVE_RUN_STATES } from "@/api/runs";

// Wire-format mirrors the DAG route in
// src/flowstack/server/routes/dag.py (P1.2b-be). Used by the GUI's
// DAGCanvas to lay out the topology before submission and (once
// the run -> flow linkage lands) the run-state overlay.

export interface DagNode {
  name: string;
  tile: string;
  upstream: string[];
  /** "scope" | "match" | null — topology badge for fanned-out nodes. */
  fanout_kind: "scope" | "match" | null;
}

export interface DagEdge {
  source: string;
  target: string;
  /** "input" | "fanout" — the canvas styles them differently. */
  kind: "input" | "fanout";
}

export interface FlowDagResponse {
  flow_name: string;
  nodes: DagNode[];
  edges: DagEdge[];
}

export function flowDagQueryKey(name: string): readonly unknown[] {
  return ["flows", "dag", name] as const;
}

export function useFlowDag(name: string, options?: { enabled?: boolean }) {
  // ``enabled`` defaults to ``Boolean(name)`` so callers that only
  // care about a name can omit the options bag. Callers that want
  // to gate the fetch on an additional condition (e.g. only fall
  // back to the structural endpoint when the overlay errors) can
  // pass ``enabled: false`` to skip the request entirely.
  const enabled = (options?.enabled ?? true) && Boolean(name);
  return useQuery({
    queryKey: flowDagQueryKey(name),
    queryFn: async (): Promise<FlowDagResponse> =>
      apiFetch<FlowDagResponse>(`/api/flows/${encodeURIComponent(name)}/dag`),
    staleTime: 5 * 60_000,
    enabled,
  });
}

// ---- Run-state overlay --------------------------------------------

/** Structural DagNode augmented with per-node run state. Mirrors
 * ``flowstack.server.routes.dag.DagNodeWithState``. */
export interface DagNodeWithState {
  name: string;
  tile: string;
  upstream: string[];
  fanout_kind: "scope" | "match" | null;
  status: LifecycleStateLabel | null;
  run_id: string | null;
  cache_hit: boolean | null;
  updated_at: string | null;
}

export interface FlowDagOverlayResponse {
  flow_name: string;
  nodes: DagNodeWithState[];
  edges: DagEdge[];
}

/** One fanout-child run linked to a Flow node. Mirrors the backend
 * ``ChildRunRow`` for the macro-collapse expansion view. Per Rupert
 * PR-284 r1: ``cache_hit`` is omitted because the per-child row only
 * renders state + run_id + relative time today. Add back when a
 * per-row cached glyph ships. */
export interface ChildRunRow {
  run_id: string;
  status: LifecycleStateLabel;
  updated_at: string;
}

export interface ChildRunsSummary {
  total: number;
  by_state: Record<string, number>;
}

export interface FlowNodeChildrenResponse {
  flow_name: string;
  node_name: string;
  summary: ChildRunsSummary;
  runs: ChildRunRow[];
}

export function flowDagOverlayQueryKey(name: string): readonly unknown[] {
  return ["flows", "dag-overlay", name] as const;
}

/** Per-node run-state overlay for the DAG canvas. Polls every 5s
 * while active states exist (or on first load); the canvas re-renders
 * with the new states. */
export function useFlowDagOverlay(name: string) {
  return useQuery({
    queryKey: flowDagOverlayQueryKey(name),
    queryFn: async (): Promise<FlowDagOverlayResponse> =>
      apiFetch<FlowDagOverlayResponse>(`/api/flows/${encodeURIComponent(name)}/dag-overlay`),
    enabled: Boolean(name),
    // Refetch every 5s while the overlay has any active-state nodes;
    // terminal-only DAGs stop polling and the GUI shows a static snapshot.
    refetchInterval: (query) => {
      const nodes = query.state.data?.nodes;
      if (!nodes) return 5_000;
      const hasActive = nodes.some((n) => n.status !== null && ACTIVE_RUN_STATES.has(n.status));
      return hasActive ? 5_000 : false;
    },
    staleTime: 4_000,
  });
}

export function flowNodeChildrenQueryKey(flowName: string, nodeName: string): readonly unknown[] {
  return ["flows", "nodes", flowName, nodeName, "children"] as const;
}

/** Per-child run list for one fanned-out Flow node. The DAG canvas's
 * macro-collapse expansion sheet calls this lazily when the user
 * clicks a fanout node. Polls every 8s while any child is active. */
export function useFlowNodeChildren(
  flowName: string,
  nodeName: string,
  options?: { enabled?: boolean }
) {
  const enabled = (options?.enabled ?? true) && Boolean(flowName) && Boolean(nodeName);
  return useQuery({
    queryKey: flowNodeChildrenQueryKey(flowName, nodeName),
    queryFn: async (): Promise<FlowNodeChildrenResponse> =>
      apiFetch<FlowNodeChildrenResponse>(
        `/api/flows/${encodeURIComponent(flowName)}/nodes/${encodeURIComponent(nodeName)}/children`
      ),
    enabled,
    refetchInterval: (query) => {
      const runs = query.state.data?.runs;
      if (!runs) return 8_000;
      const hasActive = runs.some((r) => ACTIVE_RUN_STATES.has(r.status));
      return hasActive ? 8_000 : false;
    },
    staleTime: 6_000,
  });
}
